import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const emptyForm = () => ({
  id: null,
  question: '',
  option_a: '',
  option_b: '',
  option_c: '',
  option_d: '',
  correct_option: 'A',
  weight: 1,
  min_level: 1,
  explanation: '',
});

function createKasirQuiz() {
  return {
    questions: [],
    suggestions: [],
    showSuggest: false,
    search: '',
    searchTimer: null,
    suggestTimer: null,
    loading: false,
    page: 1,
    perPage: 10,
    meta: { current_page: 1, last_page: 1, per_page: 10, total: 0, from: null, to: null },
    modal: false,
    aiModal: false,
    busy: false,
    editing: false,
    form: emptyForm(),
    aiForm: { count: 5, min_level: 1 },

    init() {
      this.fetchList();
    },

    onSearchInput() {
      const q = (this.search || '').trim();

      clearTimeout(this.suggestTimer);
      clearTimeout(this.searchTimer);

      if (q.length < 2) {
        this.suggestions = [];
        this.showSuggest = false;
        this.searchTimer = setTimeout(() => {
          this.page = 1;
          this.fetchList();
        }, 250);
        return;
      }

      this.suggestTimer = setTimeout(() => this.fetchSuggestions(q), 200);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetchList();
      }, 350);
    },

    async fetchSuggestions(q) {
      try {
        const { data } = await axios.get('/api/v1/kasir/engagement/quiz/suggest', {
          params: { q },
        });
        this.suggestions = data.data || [];
        this.showSuggest = this.suggestions.length > 0;
      } catch {
        this.suggestions = [];
        this.showSuggest = false;
      }
    },

    selectSuggestion(item) {
      this.search = item.question || '';
      this.showSuggest = false;
      this.suggestions = [];
      this.page = 1;
      this.openEdit(item);
      this.fetchList();
    },

    clearSearch() {
      this.search = '';
      this.suggestions = [];
      this.showSuggest = false;
      this.page = 1;
      this.fetchList();
    },

    async fetchList() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
        };
        const q = (this.search || '').trim();
        if (q.length >= 2) params.search = q;

        const { data } = await axios.get('/api/v1/kasir/engagement/quiz', { params });
        this.questions = data.data || [];
        this.meta = data.meta || this.meta;
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat soal.', 'error');
      } finally {
        this.loading = false;
      }
    },

    goPage(p) {
      const page = Number(p);
      if (!page || page < 1 || page > (this.meta.last_page || 1) || page === this.page) return;
      this.page = page;
      this.fetchList();
    },

    openForm() {
      this.editing = false;
      this.form = emptyForm();
      this.modal = true;
    },

    openEdit(q) {
      this.editing = true;
      this.form = {
        id: q.id,
        question: q.question || '',
        option_a: q.option_a || '',
        option_b: q.option_b || '',
        option_c: q.option_c || '',
        option_d: q.option_d || '',
        correct_option: (q.correct_option || 'A').toUpperCase(),
        weight: Number(q.weight) || 1,
        min_level: Number(q.min_level) || 1,
        explanation: q.explanation || '',
      };
      this.modal = true;
    },

    openAi() {
      this.aiForm = { count: 5, min_level: 1 };
      this.aiModal = true;
    },

    closeModals() {
      this.modal = false;
      this.aiModal = false;
      this.editing = false;
    },

    async save() {
      if (!this.form.question?.trim()) {
        window.toast?.('Pertanyaan wajib diisi.', 'error');
        return;
      }
      if (!this.form.option_a || !this.form.option_b || !this.form.option_c || !this.form.option_d) {
        window.toast?.('Semua opsi A–D wajib diisi.', 'error');
        return;
      }
      if (!['A', 'B', 'C', 'D'].includes(String(this.form.correct_option).toUpperCase())) {
        window.toast?.('Pilih jawaban benar A–D.', 'error');
        return;
      }

      this.busy = true;
      const payload = {
        question: this.form.question,
        option_a: this.form.option_a,
        option_b: this.form.option_b,
        option_c: this.form.option_c,
        option_d: this.form.option_d,
        correct_option: String(this.form.correct_option).toUpperCase(),
        weight: Number(this.form.weight) || 1,
        min_level: Number(this.form.min_level) || 1,
        explanation: this.form.explanation || null,
      };

      try {
        if (this.editing && this.form.id) {
          const { data } = await axios.put(`/api/v1/kasir/engagement/quiz/${this.form.id}`, payload);
          window.toast?.(data.message || 'Soal diperbarui.', 'success');
        } else {
          const { data } = await axios.post('/api/v1/kasir/engagement/quiz', payload);
          window.toast?.(data.message || 'Soal disimpan.', 'success');
          this.page = 1;
        }
        this.modal = false;
        this.editing = false;
        await this.fetchList();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan soal.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async generateAi() {
      this.busy = true;
      try {
        const { data } = await axios.post('/api/v1/kasir/engagement/quiz/generate-ai', {
          count: Number(this.aiForm.count) || 5,
          min_level: Number(this.aiForm.min_level) || 1,
        });
        this.aiModal = false;
        this.page = 1;
        this.search = '';
        await this.fetchList();
        window.toast?.(data.message || 'Generate selesai.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal generate AI.', 'error');
      } finally {
        this.busy = false;
      }
    },

    async remove(q) {
      const ok = window.confirmDialog
        ? await window.confirmDialog({
            title: 'Hapus soal?',
            message: q.question,
            confirmText: 'Hapus',
            variant: 'danger',
          })
        : window.confirm('Hapus soal ini?');
      if (!ok) return;

      try {
        await axios.delete('/api/v1/kasir/engagement/quiz/' + q.id);
        window.toast?.('Soal dihapus.', 'success');
        if (this.questions.length <= 1 && this.page > 1) this.page -= 1;
        await this.fetchList();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },

    async removeAll() {
      const total = this.meta.total || 0;
      const ok = window.confirmDialog
        ? await window.confirmDialog({
            title: 'Hapus SEMUA soal?',
            message: `Ini akan menghapus ${total} soal di bank quiz. Tindakan tidak bisa dibatalkan dengan mudah.`,
            confirmText: 'Hapus semua',
            variant: 'danger',
          })
        : window.confirm(`Hapus semua ${total} soal?`);
      if (!ok) return;

      this.busy = true;
      try {
        const { data } = await axios.delete('/api/v1/kasir/engagement/quiz/all');
        this.page = 1;
        this.search = '';
        this.suggestions = [];
        await this.fetchList();
        window.toast?.(data.message || 'Semua soal dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menghapus semua.', 'error');
      } finally {
        this.busy = false;
      }
    },
  };
}

Alpine.data('kasirQuiz', createKasirQuiz);
